import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

import type { ProjectLogOut, ProjectOut } from "@/entities/projects/types";
import { ProjectLog } from "@/widgets/ProjectLog";
import { PipelineSteps } from "@/widgets/ProjectPage/PipelineSteps";

export interface CollectTabProps {
  project: ProjectOut;
  /** Лайв-стрим из `useProjectStream`. */
  liveStatus: string | null;
  liveProgress: number;
  liveCurrentStep: string | null;
  liveLogs: ProjectLogOut[];
}

const TERMINAL = new Set(["done", "partial", "failed", "cancelled"]);

export function CollectTab({
  project,
  liveStatus,
  liveProgress,
  liveCurrentStep,
  liveLogs,
}: CollectTabProps): JSX.Element {
  const status = liveStatus ?? project.status;
  const progress = TERMINAL.has(status)
    ? 100
    : Math.max(project.progress, liveProgress);
  const currentStep = liveCurrentStep ?? project.current_step;

  return (
    <div className="space-y-5">
      {status === "partial" && (
        <Banner
          icon={<AlertTriangle className="h-5 w-5 text-amber" />}
          tone="amber"
          title="Сбор завершён частично"
          body="Yandex Wordstat вернул часовую квоту. Часть данных не успела попасть в результат. Нажми «Продолжить» в шапке — пайплайн доберёт оставшееся."
        />
      )}
      {status === "failed" && (
        <Banner
          icon={<XCircle className="h-5 w-5 text-pink" />}
          tone="pink"
          title="Сбор упал"
          body={project.error ?? "Подробности — в логе ниже. Можно нажать «Продолжить», чтобы перезапустить."}
        />
      )}
      {status === "done" && (
        <Banner
          icon={<CheckCircle2 className="h-5 w-5 text-green" />}
          tone="green"
          title="Готово"
          body="Кластеры собраны. Создай анализ в табах «Темы для видео», «Темы для постов», «Лидмагниты» или «Полный отчёт»."
        />
      )}
      {status === "cancelled" && (
        <Banner
          icon={<XCircle className="h-5 w-5 text-muted-foreground" />}
          tone="muted"
          title="Сбор отменён"
          body="Часть данных могла сохраниться. Можно создать новый проект или продолжить этот."
        />
      )}

      <PipelineSteps
        currentStep={currentStep}
        progress={progress}
        done={TERMINAL.has(status)}
      />

      <ProjectLog
        entries={liveLogs.length > 0 ? liveLogs : []}
        emptyText={
          status === "queued"
            ? "Ждём слот воркера…"
            : "Лог пока пустой"
        }
      />
    </div>
  );
}

function Banner({
  icon,
  title,
  body,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  tone: "amber" | "pink" | "green" | "muted";
}): JSX.Element {
  const border =
    tone === "amber"
      ? "border-amber/30 bg-amber/[0.07]"
      : tone === "pink"
        ? "border-pink/30 bg-pink/[0.07]"
        : tone === "green"
          ? "border-green/30 bg-green/[0.07]"
          : "border-white/15 bg-white/[0.04]";
  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${border}`}
    >
      <div className="mt-0.5">{icon}</div>
      <div className="space-y-1">
        <div className="font-semibold text-white">{title}</div>
        <div className="text-foreground/85">{body}</div>
      </div>
    </div>
  );
}
